import { Link } from "react-router";
import { Bell, Boxes, ClipboardList, LifeBuoy, Truck } from "lucide-react";
import { pluralize } from "@milqit/ui";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useNavCounts } from "@/lib/queries";

/** Operational alerts derived from live counts (new orders, low stock, unassigned drops). */
export function Notifications() {
  const { data } = useNavCounts();
  const alerts = data
    ? [
        data.newOrders && {
          href: "/orders?status=confirmed",
          icon: ClipboardList,
          title: `${data.newOrders} new ${data.newOrders === 1 ? "order" : "orders"} to pack`,
          text: "Confirmed and waiting — pack before the morning run.",
        },
        data.openTickets && {
          href: "/support",
          icon: LifeBuoy,
          title: `${data.openTickets} open support ${data.openTickets === 1 ? "ticket" : "tickets"}`,
          text: "Customers waiting for a reply.",
        },
        data.unassigned && {
          href: "/deliveries?agentId=none",
          icon: Truck,
          title: `${pluralize(data.unassigned, "drop")} ${data.unassigned === 1 ? "needs" : "need"} a partner`,
          text: "Tomorrow’s run isn’t fully assigned yet.",
        },
        data.lowStock && {
          href: "/inventory?status=low_stock",
          icon: Boxes,
          title: `${pluralize(data.lowStock, "item")} low or out of stock`,
          text: "Restock before tonight’s order cut-off.",
        },
      ].filter((alert): alert is Exclude<typeof alert, 0 | false> => Boolean(alert))
    : [];
  const count = alerts.length;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            aria-label={`Notifications${count ? `, ${count} new` : ""}`}
          />
        }
      >
        <Bell />
        {count ? (
          <span
            className="bg-destructive ring-background absolute top-1.5 right-1.5 size-2 rounded-full ring-2"
            aria-hidden="true"
          />
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <PopoverHeader className="border-b px-4 py-3">
          <PopoverTitle>Notifications</PopoverTitle>
          <PopoverDescription>
            {count ? "Things that need you before the morning run." : "You’re all caught up."}
          </PopoverDescription>
        </PopoverHeader>
        <ul className="divide-y">
          {alerts.map((alert) => (
            <li key={alert.href}>
              <Link
                to={alert.href}
                className="hover:bg-muted/60 flex gap-3 px-4 py-3 transition-colors"
              >
                <span className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-md">
                  <alert.icon className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium">{alert.title}</span>
                  <span className="text-muted-foreground block text-xs">{alert.text}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
